// The small map of the season timelapse: one owner frame on a canvas
// (ui-map-interface.md §3.6, R1620).
//
// The summary card and the public reader draw the frame as a picture of
// the whole sea, so it is a canvas (§1). The public reader holds no hello
// and so no terrain: the frame alone draws the map, and a ridge cell is
// its own code (R1594).
//
// The draw is the minimap's pixel walk (`fast/minimap.ts`): every pixel
// asks which hex it falls in. The walk runs ONE time a size and keeps the
// cell of each pixel, so a frame costs one pass over the pixels and one
// `putImageData`. It runs only when the frame changes: at most `fps` ×
// the speed times a second while the player plays, and never while it
// stands (§10.2).
//
// The look is the mockup's: a faction cell takes its ink at
// `cell_alpha` over the water, the line where two owners meet takes it at
// `edge_alpha`, a ridge cell takes `ridge_ink`, and no line runs along
// the rock.

import {
  PALETTE_TOKENS, TIMELAPSE_CELL_ALPHA, TIMELAPSE_EDGE_ALPHA, TIMELAPSE_RIDGE_INK,
} from '../../gen/presentation';
import { COL_PITCH, HEX_SIZE } from '../../gen/balance';
import { TIMELAPSE_RIDGE } from '../../net/timelapse';
import { deviceRatio, MAX_DPR } from '../../render/dpr';
import { ROW_PITCH, cellAt, factionColor } from '../../world';

/** The playable rectangle of a grid, in world px: `world.ts` `WORLD` and
 *  `WORLD_H` of that grid, the server's `Grid.WorldPx` and
 *  `Grid.WorldHeightPx` (R395). */
export function gridSize(cols: number, rows: number): [number, number] {
  return [cols * COL_PITCH, ROW_PITCH * (rows - 1) + 2 * HEX_SIZE];
}

/**
 * The cell under each pixel of a `w` × `h` picture of the grid, -1 for a
 * pixel off the map. The grid is fitted into the picture and centred.
 */
export function pixelCells(w: number, h: number, cols: number, rows: number): Int32Array {
  const [gw, gh] = gridSize(cols, rows);
  const scale = Math.min(w / gw, h / gh);
  const ox = (w - gw * scale) / 2;
  const oy = (h - gh * scale) / 2;
  const out = new Int32Array(w * h);
  const inv = 1 / scale;
  for (let py = 0; py < h; py++) {
    const wy = (py + 0.5 - oy) * inv;
    for (let px = 0; px < w; px++) {
      const wx = (px + 0.5 - ox) * inv;
      out[py * w + px] = wx < 0 || wy < 0 || wx > gw || wy > gh ? -1 : cellAt(wx, wy, cols, rows);
    }
  }
  return out;
}

/** The inks of one paint, as packed 0xrrggbb. */
export interface FrameInks {
  water: number;
  ridge: number;
  /** The ink of each owner code, 256 entries; index 0 is unused. */
  faction: Uint32Array;
  cellAlpha: number;
  edgeAlpha: number;
}

/** The game's own inks: the chrome's deep water, the faction palette. */
function gameInks(): FrameInks {
  const [r, g, b] = PALETTE_TOKENS['--deep-rgb'].split(' ').map(Number);
  const faction = new Uint32Array(256);
  for (let code = 1; code < faction.length; code++) faction[code] = factionColor(code);
  return {
    water: (r << 16) | (g << 8) | b,
    ridge: TIMELAPSE_RIDGE_INK,
    faction,
    cellAlpha: TIMELAPSE_CELL_ALPHA,
    edgeAlpha: TIMELAPSE_EDGE_ALPHA,
  };
}

/** One channel of `ink` laid over the water at `a`. */
function over(water: number, ink: number, a: number, shift: number): number {
  const w = (water >> shift) & 0xff;
  return Math.round(w + (((ink >> shift) & 0xff) - w) * a);
}

/**
 * Paints one frame into an RGBA buffer of `w` × `h` pixels, every pixel
 * opaque. A pixel is an owner line when a pixel beside it, left, right,
 * above or below, falls in a cell of another owner or off the map; a
 * ridge beside it draws no line. Pure, so a test reads the buffer.
 */
export function paintFrame(
  out: Uint8ClampedArray, cellOf: Int32Array, w: number, h: number,
  cells: Uint8Array, inks: FrameInks,
): void {
  const n = cells.length;
  const codeAt = (p: number): number => {
    const c = cellOf[p];
    return c < 0 || c >= n ? -1 : cells[c];
  };
  /** True when the pixel `q` beside an owned pixel ends its owner. */
  const ends = (q: number, code: number): boolean => {
    const o = codeAt(q);
    return o !== code && o !== TIMELAPSE_RIDGE;
  };
  for (let py = 0; py < h; py++) {
    for (let px = 0; px < w; px++) {
      const p = py * w + px;
      const code = codeAt(p);
      let ink = inks.water;
      let a = 1;
      if (code === TIMELAPSE_RIDGE) {
        ink = inks.ridge;
      } else if (code > 0) {
        ink = inks.faction[code];
        const edge = px === 0 || px === w - 1 || py === 0 || py === h - 1
          || ends(p - 1, code) || ends(p + 1, code) || ends(p - w, code) || ends(p + w, code);
        a = edge ? inks.edgeAlpha : inks.cellAlpha;
      }
      const at = p * 4;
      out[at] = a === 1 ? (ink >> 16) & 0xff : over(inks.water, ink, a, 16);
      out[at + 1] = a === 1 ? (ink >> 8) & 0xff : over(inks.water, ink, a, 8);
      out[at + 2] = a === 1 ? ink & 0xff : over(inks.water, ink, a, 0);
      out[at + 3] = 255;
    }
  }
}

/**
 * One canvas that shows one frame. The CSS sets its size on the page; the
 * backing store follows the laid-out width at the next draw, so a phone
 * column and a desktop box both draw sharp.
 */
export class FrameCanvas {
  readonly el: HTMLCanvasElement;
  private cellOf: Int32Array | null = null;
  private key = '';
  private img: ImageData | null = null;
  private readonly inks = gameInks();

  /** `fallbackPx` is the size before the canvas has a layout. */
  constructor(private readonly fallbackPx: number) {
    this.el = document.createElement('canvas');
    this.el.className = 'tlc';
  }

  /** Draws one frame of a `cols` × `rows` grid. */
  draw(cells: Uint8Array, cols: number, rows: number): void {
    const css = this.el.clientWidth > 0 ? this.el.clientWidth : this.fallbackPx;
    // Device px for one CSS px, capped as the map's own camera caps it.
    const dpr = deviceRatio(MAX_DPR);
    const side = Math.max(1, Math.round(css * dpr));
    const key = `${side}:${cols}:${rows}`;
    if (key !== this.key) {
      this.key = key;
      this.el.width = side;
      this.el.height = side;
      this.cellOf = pixelCells(side, side, cols, rows);
      this.img = null;
    }
    const ctx = this.el.getContext('2d');
    if (!ctx || !this.cellOf) return;
    if (!this.img) this.img = ctx.createImageData(side, side);
    paintFrame(this.img.data, this.cellOf, side, side, cells, this.inks);
    ctx.putImageData(this.img, 0, 0);
  }
}
